import { useState, useEffect } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface CookieConsent {
  version: string;
  analyticsEnabled: boolean;
  timestamp: number;
}

const CONSENT_KEY = "ss_cookie_consent";
const CONSENT_VERSION = "1.0";

const texts = {
  en: {
    title: "Cookie usage",
    desc: "We use our own and third-party cookies to ensure the site works correctly and to obtain usage statistics that help us improve your experience. You can accept all cookies, reject non-essential ones, or configure your preferences. For more information, see our",
    policyLabel: "Cookie Policy",
    policyLink: "/en/cookies",
    configure: "Configure",
    reject: "Reject non-essential",
    accept: "Accept all",
    configTitle: "Cookie settings",
    configDesc: "Customize the cookies you want to allow on this site.",
    necessary: "Necessary cookies",
    necessaryDesc: "Always active. Required for the basic functioning of the site.",
    analytics: "Analytics cookies",
    analyticsDesc: "Help us understand how the site is used to improve it.",
    cancel: "Cancel",
    save: "Save preferences",
  },
  es: {
    title: "Uso de cookies",
    desc: "Utilizamos cookies propias y de terceros para que el sitio funcione correctamente y para obtener estadísticas de uso que nos ayuden a mejorar su experiencia. Puede aceptar todas las cookies, rechazar las no esenciales o configurar sus preferencias. Para más información, consulte nuestra",
    policyLabel: "Política de Cookies",
    policyLink: "/es/cookies",
    configure: "Configurar",
    reject: "Rechazar no esenciales",
    accept: "Aceptar todas",
    configTitle: "Configuración de cookies",
    configDesc: "Personaliza las cookies que deseas permitir en este sitio.",
    necessary: "Cookies necesarias",
    necessaryDesc: "Siempre activas. Necesarias para el funcionamiento básico del sitio.",
    analytics: "Cookies de análisis",
    analyticsDesc: "Nos ayudan a comprender cómo se utiliza el sitio para mejorarlo.",
    cancel: "Cancelar",
    save: "Guardar preferencias",
  },
  fr: {
    title: "Utilisation des cookies",
    desc: "Nous utilisons des cookies propres et de tiers pour assurer le bon fonctionnement du site et obtenir des statistiques d'utilisation afin d'améliorer votre expérience. Vous pouvez accepter tous les cookies, refuser les non essentiels ou configurer vos préférences. Pour plus d'informations, consultez notre",
    policyLabel: "Politique de Cookies",
    policyLink: "/fr/cookies",
    configure: "Configurer",
    reject: "Refuser non essentiels",
    accept: "Tout accepter",
    configTitle: "Paramètres des cookies",
    configDesc: "Personnalisez les cookies que vous souhaitez autoriser sur ce site.",
    necessary: "Cookies nécessaires",
    necessaryDesc: "Toujours actifs. Nécessaires au fonctionnement de base du site.",
    analytics: "Cookies d'analyse",
    analyticsDesc: "Nous aident à comprendre l'utilisation du site pour l'améliorer.",
    cancel: "Annuler",
    save: "Enregistrer les préférences",
  },
};

type Locale = "en" | "es" | "fr";

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const location = useLocation();

  // Detect locale from URL
  const pathLocale = location.pathname.split("/")[1] as Locale;
  const locale: Locale = ["en", "es", "fr"].includes(pathLocale) ? pathLocale : "en";
  const t = texts[locale];

  useEffect(() => {
    const consentString = localStorage.getItem(CONSENT_KEY);
    if (consentString) {
      try {
        const consent: CookieConsent = JSON.parse(consentString);
        if (consent.version === CONSENT_VERSION) {
          setAnalyticsEnabled(consent.analyticsEnabled);
          return;
        }
      } catch {
        // Invalid consent, show banner
      }
    }
    const timer = setTimeout(() => setShowBanner(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const saveConsent = (analytics: boolean) => {
    const consent: CookieConsent = {
      version: CONSENT_VERSION,
      analyticsEnabled: analytics,
      timestamp: Date.now(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setAnalyticsEnabled(analytics);
    setShowBanner(false);
    setShowConfig(false);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <m.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border shadow-2xl"
          >
            <div className="max-w-7xl mx-auto px-6 md:px-8 py-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">{t.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t.desc}{" "}
                    <Link to={t.policyLink} className="text-primary hover:underline">
                      {t.policyLabel}
                    </Link>
                    .
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-3 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} className="text-xs">
                    {t.configure}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => saveConsent(false)} className="text-xs">
                    {t.reject}
                  </Button>
                  <Button size="sm" onClick={() => saveConsent(true)} className="text-xs">
                    {t.accept}
                  </Button>
                </div>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>

      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.configTitle}</DialogTitle>
            <DialogDescription>{t.configDesc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between space-x-4 rounded-lg border border-border p-4 bg-muted/50">
              <div className="flex-1 space-y-1">
                <Label htmlFor="necessary" className="text-sm font-medium">{t.necessary}</Label>
                <p className="text-xs text-muted-foreground">{t.necessaryDesc}</p>
              </div>
              <Switch id="necessary" checked disabled />
            </div>
            <div className="flex items-center justify-between space-x-4 rounded-lg border border-border p-4">
              <div className="flex-1 space-y-1">
                <Label htmlFor="analytics" className="text-sm font-medium">{t.analytics}</Label>
                <p className="text-xs text-muted-foreground">{t.analyticsDesc}</p>
              </div>
              <Switch id="analytics" checked={analyticsEnabled} onCheckedChange={setAnalyticsEnabled} />
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setShowConfig(false)} size="sm">{t.cancel}</Button>
            <Button onClick={() => saveConsent(analyticsEnabled)} size="sm">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieBanner;
